import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ThingFuel,
  ThingTicket,
  ThingStamp,
  ActivityTennis,
  EventHistorical,
  ThingMovie,
  ActivityMusic,
  ActivityScience,
  ActivityFootball,
  ActivityBasketball,
  ActivityBaseball,
  ActivitySoccer,
  ThingFlag,
  DiscoveryPopulation,
  DiscoveryHeritage,
  DiscoveryNameMeaning,
  PlaceTemple,
  PlaceWorld,
} from '@fs/zion-icon'
import useEP from '../../../shared/hooks/useEP'
import { getTileData, formatTileContentData, getTileContentData } from '../services/services'
import { getCountryLanguageSelectOptions } from '../services/aamRegionLanguage'
import { newAncestorTemplate } from '../../../shared/helpers/person_Helper'

export const useTileData = () => {
  const [state, dispatch, func] = useEP(getTileData)

  return [state, func, dispatch]
}

export const useRegionLanguage = (t) => {
  const [regionLanguage, setRegionLanguage] = useState([])
  useEffect(() => {
    const regionLanguageOptions = getCountryLanguageSelectOptions(t)
    setRegionLanguage(regionLanguageOptions)
  }, [t])

  return regionLanguage
}

export const useTileContentData = ({ tileData, autoLoad = true }) => {
  const [state, dispatch, fn] = useEP(getTileContentData)

  useEffect(() => {
    if (autoLoad) {
      dispatch({
        type: 'load',
      })
    }
  }, [autoLoad, dispatch])

  useEffect(() => {
    if (state.load) {
      getTileContentData({ ...formatTileContentData(tileData) }).then((res) => {
        dispatch({ type: 'complete', res })
      })
    }
  }, [dispatch, state, tileData])

  return [state, dispatch, fn]
}

export const useAncestorYearMatch = (treeDataState, personDetailsState, tileDataState, userPersonId) => {
  const [t] = useTranslation()
  const [data, setData] = useState()

  useEffect(() => {
    if (treeDataState.complete && personDetailsState.complete && tileDataState.complete) {
      const { birthDate } = personDetailsState.res
      const { region, language } = tileDataState.res[0]

      const ancestorTiles = []
      treeDataState.res.persons.forEach((person) => {
        if (person.personId !== userPersonId) {
          const personYears = person?.lifespan.split('-')
          if (parseInt(personYears[0], 10) === parseInt(birthDate, 10)) {
            ancestorTiles.push(
              newAncestorTemplate(
                birthDate,
                person,
                region,
                language,
                `${person?.name} ${person?.surname}`,
                t('aam.year-picker.ancestor-birth')
              )
            )
          } else if (parseInt(personYears[1], 10) === parseInt(birthDate, 10)) {
            ancestorTiles.push(
              newAncestorTemplate(
                birthDate,
                person,
                region,
                language,
                `${person?.name} ${person?.surname}`,
                t('aam.year-picker.ancestor-death')
              )
            )
          }
        }
      })
      setData(ancestorTiles)
    }
  }, [
    personDetailsState.complete,
    personDetailsState.res,
    t,
    tileDataState,
    tileDataState.complete,
    treeDataState.complete,
    treeDataState.res,
    userPersonId,
  ])

  return data
}

export const useInnerTileVisuals = (imageUrl, color) => {
  const tileType = imageUrl ? imageUrl.split('/') : ''

  const cdnBaseUrl = 'https://common-discovery-service-pgp.familysearchcdn.org/aam/'
  let icon
  let background
  switch (tileType[tileType.length - 1]) {
    case 'icon_gas.svg':
      background = `Gas.jpg`
      icon = <ThingFuel color={color} size="md" />
      break
    case 'icon_movie_ticket.svg':
      background = `MovieTicket.jpg`
      icon = <ThingTicket color={color} size="md" />
      break
    case 'icon_stamp.svg':
      background = `Stamps.jpg`
      icon = <ThingStamp color={color} size="md" />
      break
    case 'icon_tennis.svg':
      background = `Tennis.jpg`
      icon = <ActivityTennis color={color} size="md" />
      break
    case 'icon_news.svg':
      background = `News.jpg`
      icon = <EventHistorical color={color} size="md" />
      break
    case 'icon_movies.svg':
      background = `Movies.jpg`
      icon = <ThingMovie color={color} size="md" />
      break
    case 'icon_music.svg':
      background = `Music.jpg`
      icon = <ActivityMusic color={color} size="md" />
      break
    case 'icon_tech.svg':
      background = `Technology.jpg`
      icon = <ActivityScience color={color} size="md" />
      break
    case 'icon_football.svg':
      background = `Football.jpg`
      icon = <ActivityFootball color={color} size="md" />
      break
    case 'icon_basketball.svg':
      background = `Basketball.jpg`
      icon = <ActivityBasketball color={color} size="md" />
      break
    case 'icon_baseball.svg':
      background = `Baseball.jpg`
      icon = <ActivityBaseball color={color} size="md" />
      break
    case 'icon_soccer.svg':
      background = `Soccer.jpg`
      icon = <ActivitySoccer color={color} size="md" />
      break
    case 'icon_president.svg':
      background = `President.jpg`
      icon = <ThingFlag color={color} size="md" />
      break
    case 'icon_population-growth.svg':
      background = `Population.jpg`
      icon = <DiscoveryPopulation color={color} size="md" />
      break
    case 'icon_temple_white.svg':
      background = `Temples.jpg`
      icon = <PlaceTemple color={color} size="md" />
      break
    case 'icon_temple_dedication_white.svg':
      background = `Temples.jpg`
      icon = <PlaceTemple color={color} size="md" />
      break
    case 'icon_prophet.svg':
      background = `Prophets.jpg`
      icon = <PlaceTemple color={color} size="md" />
      break
    case 'icon_world.svg':
      background = `Population.jpg`
      icon = <PlaceWorld color={color} size="md" />
      break
    case 'icon_heritage.svg':
      background = ``
      icon = <DiscoveryHeritage color={color} size="md" />
      break
    default:
      background = ``
      icon = <DiscoveryNameMeaning color={color} size="md" />
      break
  }

  const tileVisual = {
    icon,
    background: `${cdnBaseUrl}${background}`,
  }
  return tileVisual
}

export const useTileVisibility = (tileRef) => {
  const [isHidden, setIsHidden] = useState(true)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio > 0 || (entry.isIntersecting && tileRef.current)) {
            setIsHidden(false)
          } else {
            setIsHidden(true)
          }
        })
      },
      {
        threshold: 0.5,
        rootMargin: '25px',
      }
    )
    if (tileRef.current) {
      observer.observe(tileRef.current)
    }
    return () => {
      const { current: canceledRef } = tileRef
      if (observer && observer.unobserve) {
        observer.unobserve(canceledRef)
      }
    }
  }, [tileRef])

  return isHidden
}

export const useCorrections = () => {
  const location = useLocation()
  const splitLocation = location.pathname.split('/')
  if (splitLocation.includes('corrections') || splitLocation.includes('correctionslogin')) {
    return true
  }
  return false
}

export const useComparisonByYear = (aamState, comparisonTargetPerson, dscState, tileData, comparisonYear) => {
  if (
    (aamState.isGuestUser &&
      comparisonTargetPerson &&
      comparisonTargetPerson.firstName !== dscState.nonstandardUser.firstName) ||
    (tileData?.personId && comparisonTargetPerson?.personId && tileData.personId !== comparisonTargetPerson.personId) ||
    (comparisonYear && tileData?.year && comparisonYear !== tileData?.year)
  ) {
    return true
  }
  return false
}

export const useComparisonByName = (
  aamState,
  comparisonTargetPerson,
  dscState,
  tileData,
  parsedNames,
  comparisonParsedNames
) => {
  if (
    (aamState?.isGuestUser &&
      comparisonTargetPerson &&
      comparisonTargetPerson.firstName !== dscState.nonstandardUser.firstName) ||
    (tileData?.personId && comparisonTargetPerson?.personId && tileData.personId !== comparisonTargetPerson.personId) ||
    (parsedNames?.firstName?.name &&
      comparisonParsedNames?.firstName?.name &&
      parsedNames?.firstName?.name !== comparisonParsedNames?.firstName?.name) ||
    (parsedNames?.lastName?.name &&
      comparisonParsedNames?.lastName?.name &&
      parsedNames?.lastName?.name !== comparisonParsedNames?.lastName?.name)
  ) {
    return true
  }
  return false
}
